/* oxlint-disable typescript/no-explicit-any */
import { IEvent, IConsumer, createConsumer, createSubscriber } from '@cian/events-log';
import { createAsyncThunk, ThunkAction, Action } from '@reduxjs/toolkit';

import { getIsAuthenticated } from '../../selectors/user';
import { resolveUser } from '../../services/user';
import { setUser } from '../../slices/userReduces';
import { IThunkApi } from '../../types/redux';
import { resolveUserRole } from '../../services/userRole';
import { setUserRole } from '../../slices/userRoleReducer';

export interface IRequestAuthenticationParameters {
  onSuccess?(): Action | ThunkAction<void, any, unknown, Action>;
  onCloseNoAuth?(): Action | ThunkAction<void, any, unknown, Action>;
}

const TOPIC_USER = 'user';

type TConsumerSingleton = (() => IConsumer) & { _instance?: IConsumer };

const getUserConsumer: TConsumerSingleton = (): IConsumer => {
  if (!getUserConsumer._instance) {
    getUserConsumer._instance = createConsumer({ topic: TOPIC_USER });
  }

  return getUserConsumer._instance;
};

export const requestAuthentication = createAsyncThunk<void, IRequestAuthenticationParameters, IThunkApi>(
  'authentication/requestAuthentication',
  async (parameters, thunkApi) => {
    const { dispatch, extra: context, getState } = thunkApi;

    /* istanbul ignore else */
    if (typeof window === 'undefined') {
      return;
    }

    if (getIsAuthenticated(getState())) {
      /* istanbul ignore else */
      if (parameters?.onSuccess) {
        dispatch(parameters.onSuccess());
      }

      return;
    }

    const consumer = getUserConsumer();

    const userSubscriber = createSubscriber(async (event: IEvent<unknown>) => {
      if (event.type === 'authenticated') {
        const { httpApi, logger, config } = context;

        const user = await resolveUser({ config, httpApi, logger });
        const userRole = await resolveUserRole({ config, httpApi, logger });

        dispatch(setUser(user));
        dispatch(setUserRole(userRole));

        /* istanbul ignore else */
        if (parameters?.onSuccess) {
          dispatch(parameters.onSuccess());
        }

        consumer.unsubscribe(userSubscriber);

        return;
      }

      if (event.type === 'authentication_modal_closed') {
        /* istanbul ignore else */
        if (parameters?.onCloseNoAuth) {
          dispatch(parameters.onCloseNoAuth());
        }

        consumer.unsubscribe(userSubscriber);
      }
    });

    consumer.subscribe(userSubscriber);

    /* istanbul ignore else */
    if (context.microfrontendsAPI) {
      await context.microfrontendsAPI.header.openAuthModal({
        autoRefresh: false,
      });
    }
  },
);
